import {
  useState,
  useContext,
  useCallback,
  createContext,
  type ReactNode,
  useLayoutEffect,
} from 'react';

import { OfflineModeIndicator } from './offline-mode-indicator';

type RegisterSuppression = () => () => void;

const OfflineModeIndicatorSuppressionContext = createContext<RegisterSuppression | null>(null);

type ScopeProps = {
  children: ReactNode;
  visible: boolean;
};

/**
 * Owns the global offline indicator and lets a mounted route replace that
 * indicator with route-specific chrome. Registrations are counted so future
 * nested suppressors cannot make the indicator reappear prematurely.
 */
export function OfflineModeIndicatorScope({ children, visible }: ScopeProps) {
  const [suppressionCount, setSuppressionCount] = useState(0);

  const registerSuppression = useCallback<RegisterSuppression>(() => {
    let released = false;
    setSuppressionCount((count) => count + 1);

    return () => {
      if (released) return;
      released = true;
      setSuppressionCount((count) => Math.max(0, count - 1));
    };
  }, []);

  return (
    <OfflineModeIndicatorSuppressionContext.Provider value={registerSuppression}>
      {children}
      {visible && suppressionCount === 0 ? <OfflineModeIndicator /> : null}
    </OfflineModeIndicatorSuppressionContext.Provider>
  );
}

/**
 * Suppresses the global offline indicator for this component's mounted
 * lifetime. A layout effect prevents the indicator painting for cold offline
 * entries while cleanup restores it as soon as the route unmounts.
 */
export function useSuppressOfflineModeIndicator() {
  const registerSuppression = useContext(OfflineModeIndicatorSuppressionContext);

  useLayoutEffect(() => {
    if (!registerSuppression) return undefined;
    return registerSuppression();
  }, [registerSuppression]);
}
